import * as React from 'react';
import { useDispatch } from 'react-redux';
import { fetchTrackingCategoriesRequest } from 'store/organisation/actions';
import type { Organisation } from 'store/organisation/models';
import { shouldFetchOrganisationProviderData } from 'utils/get-provider-type';
import {
  getTrackingCategoriesRetryDelayMs,
  shouldSkipTrackingCategoriesBurst,
} from 'utils/trackingCategoriesFetchGuard';

/**
 * Load provider tracking categories for an entity, and retry if the first
 * response is dropped (org list not hydrated yet) or the request fails.
 * An empty successful payload is stored as `[]` and stops retrying.
 */
export function useFetchTrackingCategories(
  organisationId: string | undefined | null,
  organisation: Organisation | null | undefined,
  enabled: boolean
): void {
  const dispatch = useDispatch();
  const trackingLoaded = organisation?.trackingCategories !== undefined;

  React.useEffect(() => {
    const id = organisationId?.trim() ?? '';
    if (!enabled || !id) return;
    if (!organisation) return;
    if (!shouldFetchOrganisationProviderData(organisation)) return;
    if (trackingLoaded) return;

    let cancelled = false;
    const tryFetch = () => {
      if (cancelled) return;
      if (shouldSkipTrackingCategoriesBurst?.(id)) return;
      dispatch(fetchTrackingCategoriesRequest(id));
    };

    tryFetch();
    const retryMs = getTrackingCategoriesRetryDelayMs?.(id) ?? 1600;
    const retrySoon = window.setTimeout(tryFetch, retryMs);
    const retryLater = window.setTimeout(tryFetch, retryMs + 4000);

    return () => {
      cancelled = true;
      window.clearTimeout(retrySoon);
      window.clearTimeout(retryLater);
    };
  }, [
    dispatch,
    organisationId,
    enabled,
    organisation,
    trackingLoaded,
    organisation?.connectedProperly,
    organisation?.providerName,
  ]);
}
